import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  HeartHandshake,
  Sparkles,
  Compass,
  Wallet,
  Home,
  MessageSquareHeart,
  Play,
  RotateCcw,
  History,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  ChevronRight,
  UserCheck,
  Award,
  BarChart3,
  ArrowLeft,
  X,
  Flame,
  ShieldAlert,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, detailText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PremiumPaymentModal from "@/components/app/PremiumPaymentModal";
import { toast } from "sonner";

const EMOJI_RATINGS = [
  { value: 1, emoji: "😕", label: "Kurang Terbuka", desc: "1 Poin" },
  { value: 2, emoji: "😐", label: "Cukup Dijawab", desc: "2 Poin" },
  { value: 3, emoji: "😊", label: "Jawaban Hangat", desc: "3 Poin" },
  { value: 4, emoji: "🥰", label: "Sangat Menyentuh", desc: "4 Poin" },
];

const DEFAULT_CATEGORIES = [
  { id: "relationship", name: "Hubungan & Emosi", description: "Perasaan, apresiasi, dan kenangan indah bersama pasangan.", icon: "HeartHandshake" },
  { id: "future", name: "Rencana Masa Depan", description: "Impian, cita-cita, tempat tinggal, dan visi hidup berdua.", icon: "Compass" },
  { id: "wedding", name: "Persiapan Pernikahan", description: "Harapan, kekhawatiran, dan hal berkesan seputar momen pernikahan.", icon: "Sparkles" },
  { id: "finance", name: "Keuangan & Anggaran Rumah Tangga", description: "Pengelolaan uang, prioritas tabungan, dan kebiasaan finansial.", icon: "Wallet" },
  { id: "family", name: "Keluarga & Kehidupan Pernikahan", description: "Hubungan dengan mertua, rencana momongan, dan peran di rumah.", icon: "Home" },
  { id: "conflict", name: "Resolusi Konflik & Komunikasi", description: "Gaya komunikasi saat emosi, cara meminta maaf, dan penyelesaian masalah.", icon: "MessageSquareHeart" },
];

const getCategoryIcon = (iconName) => {
  switch (iconName) {
    case "Compass": return Compass;
    case "Sparkles": return Sparkles;
    case "Wallet": return Wallet;
    case "Home": return Home;
    case "MessageSquareHeart": return MessageSquareHeart;
    default: return HeartHandshake;
  }
};

const markInviteDismissed = (sessionId) => {
  if (!sessionId) return;
  try {
    window.sessionStorage.setItem(`weddly_ct_dismissed_${sessionId}`, "1");
  } catch {}
};

const isInviteDismissed = (sessionId) => {
  if (!sessionId) return false;
  try {
    return window.sessionStorage.getItem(`weddly_ct_dismissed_${sessionId}`) === "1";
  } catch {
    return false;
  }
};

export default function CoupleTalk() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState("lobby"); // lobby | game | recap | history
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [selectedCat, setSelectedCat] = useState("relationship");
  const [activeSession, setActiveSession] = useState(null);
  const [recapData, setRecapData] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [hasPartners, setHasPartners] = useState(true);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Dialogs state
  const [isFlipped, setIsFlipped] = useState(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState(3);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [invitationOpen, setInvitationOpen] = useState(false);
  const [paymodalOpen, setPaymodalOpen] = useState(false);
  const [selectedHistoryRecap, setSelectedHistoryRecap] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const currCardIdx = activeSession?.current_card_index;
  const currSessionId = activeSession?.session_id;

  // Auto flip card open only for asking player when turn index changes
  useEffect(() => {
    setIsFlipped(false);
    if (!activeSession) return;
    const cards = activeSession.cards || [];
    const currIdx = activeSession.current_card_index ?? 0;
    const currentCard = cards[currIdx];
    if (!currentCard) return;

    const isAsking = currentCard.asking_user_id === user?.user_id || currentCard.asking_user_id === "usr_partner_placeholder";

    if (isAsking) {
      const timer = setTimeout(() => {
        setIsFlipped(true);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [currCardIdx, currSessionId, user?.user_id]);

  // Polling for active session & turn updates
  useEffect(() => {
    let timer;
    if (view === "lobby" || view === "game") {
      timer = setInterval(async () => {
        try {
          const actRes = await api.get("/couple-talk/active");
          const sess = actRes.data?.session;
          if (sess) {
            setActiveSession(sess);
            if (
              view === "lobby" &&
              sess.created_by !== user?.user_id &&
              sess.status === "in_progress" &&
              !isInviteDismissed(sess.session_id)
            ) {
              setInvitationOpen(true);
            }
            if (view === "game" && sess.status === "completed") {
              fetchRecap(sess.session_id);
            }
          } else {
            // Active session was ended/abandoned while user is in game view
            if (view === "game") {
              setActiveSession(null);
              setView("lobby");
              toast.info("Sesi permainan telah diakhiri oleh pasangan Anda.");
            }
          }
        } catch {}
      }, 2000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [view, user?.user_id]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // 1. Check partner linking
      const pRes = await api.get("/partners");
      const partners = pRes.data?.partners || [];
      setHasPartners(partners.length > 0);

      // 2. Fetch categories
      const catRes = await api.get("/couple-talk/categories");
      const fetchedCats = catRes.data?.categories || [];
      if (fetchedCats.length > 0) {
        setCategories(fetchedCats);
      }

      // 3. Fetch active session
      const actRes = await api.get("/couple-talk/active");
      if (actRes.data?.session) {
        setActiveSession(actRes.data.session);
      }
    } catch (err) {
      toast.error(detailText(err.response?.data?.detail) || "Gagal memuat data Couple Talk");
    } finally {
      setLoading(false);
    }
  };

  const handleStartGame = async () => {
    if (!selectedCat) {
      toast.error("Pilih 1 topik obrolan!");
      return;
    }
    const isTrial = !isAdmin && user?.plan_type === "trial";
    if (isTrial && selectedCat !== "relationship") {
      setPaymodalOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/couple-talk/start", { categories: [selectedCat] });
      setActiveSession(res.data.session);
      setView("game");
      toast.success("Permainan Couple Talk dimulai! Selamat mengobrol ❤️");
    } catch (err) {
      if (err.response?.status === 403 && isTrial) {
        setPaymodalOpen(true);
      } else {
        toast.error(detailText(err.response?.data?.detail) || "Gagal memulai permainan");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGameAction = async (action, ratingVal = null) => {
    if (!activeSession) return;
    const currCard = activeSession.cards[activeSession.current_card_index];
    if (!currCard) return;

    setSubmitting(true);
    try {
      const res = await api.post(`/couple-talk/sessions/${activeSession.session_id}/action`, {
        card_index: activeSession.current_card_index,
        action,
        rating: ratingVal,
      });

      const updated = res.data.session;
      setActiveSession(updated);
      setRatingDialogOpen(false);
      setDeclineDialogOpen(false);

      if (updated.status === "completed") {
        fetchRecap(updated.session_id);
      } else {
        toast.success("Giliran diperbarui!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memproses aksi");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEndGame = async () => {
    if (!activeSession) return;
    setSubmitting(true);
    try {
      markInviteDismissed(activeSession.session_id);
      await api.post(`/couple-talk/sessions/${activeSession.session_id}/end`);
      setActiveSession(null);
      setEndDialogOpen(false);
      setView("lobby");
      toast.success("Sesi permainan diakhiri.");
    } catch (err) {
      toast.error(detailText(err.response?.data?.detail) || "Gagal mengakhiri permainan");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchRecap = async (sessionId) => {
    try {
      const res = await api.get(`/couple-talk/recap/${sessionId}`);
      setRecapData(res.data.recap);
      setView("recap");
    } catch (err) {
      toast.error("Gagal memuat rekap permainan");
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get("/couple-talk/history");
      setHistoryList(res.data?.history || []);
      setView("history");
    } catch (err) {
      toast.error("Gagal memuat riwayat permainan");
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (catId) => {
    if (selectedCats.includes(catId)) {
      if (selectedCats.length === 1) {
        toast.error("Minimal harus memilih 1 kategori!");
        return;
      }
      setSelectedCats(selectedCats.filter((id) => id !== catId));
    } else {
      setSelectedCats([...selectedCats, catId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedCats.length === categories.length) {
      setSelectedCats([categories[0]?.id || "relationship"]);
    } else {
      setSelectedCats(categories.map((c) => c.id));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center space-y-4">
        <Sparkles className="h-10 w-10 animate-spin text-rose-500" />
        <p className="text-sm font-medium text-slate-600">Menyiapkan Couple Talk…</p>
      </div>
    );
  }

  // ==================== VIEW: RECAP ====================
  if (view === "recap" && recapData) {
    const { player_a, player_b, overall, category_stats } = recapData;
    return (
      <div className="mx-auto max-w-3xl space-y-6 px-4 sm:px-6 pb-24 sm:pb-12">
        <Card className="border-rose-200 bg-gradient-to-br from-rose-50/70 via-white to-pink-50/50 shadow-md">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <HeartHandshake className="h-8 w-8" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              Couple Connection Recap 🎉
            </CardTitle>
            <CardDescription className="text-sm text-slate-600">
              Selamat! Kalian telah menyelesaikan 20 pertanyaan Couple Talk.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Score Comparison Board */}
            <div className="grid grid-cols-2 gap-4 rounded-xl bg-white p-4 shadow-sm border border-slate-100">
              <div className="text-center p-3 rounded-lg bg-rose-50/60 border border-rose-100">
                <p className="text-xs font-semibold text-rose-700 uppercase tracking-wider">{player_a.name}</p>
                <p className="text-3xl font-bold text-rose-600 my-1">{player_a.total_score}</p>
                <p className="text-xs text-slate-500">Poin Diterima</p>
                <div className="mt-2 text-xs text-slate-600 border-t border-rose-100 pt-2 space-y-1">
                  <div>Dijawab: <span className="font-semibold text-slate-800">{player_a.answered_count}</span></div>
                  <div>Rata-rata Rating: <span className="font-semibold text-rose-600">⭐ {player_a.avg_rating}</span></div>
                </div>
              </div>

              <div className="text-center p-3 rounded-lg bg-pink-50/60 border border-pink-100">
                <p className="text-xs font-semibold text-pink-700 uppercase tracking-wider">{player_b.name}</p>
                <p className="text-3xl font-bold text-pink-600 my-1">{player_b.total_score}</p>
                <p className="text-xs text-slate-500">Poin Diterima</p>
                <div className="mt-2 text-xs text-slate-600 border-t border-pink-100 pt-2 space-y-1">
                  <div>Dijawab: <span className="font-semibold text-slate-800">{player_b.answered_count}</span></div>
                  <div>Rata-rata Rating: <span className="font-semibold text-pink-600">⭐ {player_b.avg_rating}</span></div>
                </div>
              </div>
            </div>

            {/* Overall Stats summary */}
            <div className="grid grid-cols-3 gap-3 text-center text-xs">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-slate-700">Total Dijawab</p>
                <p className="text-lg font-bold text-emerald-600">{overall.total_answered} / 20</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-slate-700">Ditolak (-7)</p>
                <p className="text-lg font-bold text-rose-600">{overall.total_declined}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-slate-700">Dilewati (-5)</p>
                <p className="text-lg font-bold text-amber-600">{overall.total_skipped}</p>
              </div>
            </div>

            {/* Category Stats */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-rose-500" /> Ringkasan Per Kategori
              </h4>
              <div className="space-y-2">
                {category_stats.map((cat) => (
                  <div key={cat.category_id} className="flex items-center justify-between text-xs p-2.5 rounded-lg bg-slate-50/80 border border-slate-100">
                    <span className="font-medium text-slate-700">{cat.category_name}</span>
                    <div className="flex items-center gap-4 text-slate-600">
                      <span>{cat.answered_count} / {cat.total_questions} Kartu</span>
                      <span className="font-semibold text-rose-600">⭐ {cat.avg_rating}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reflection Note */}
            <div className="rounded-lg bg-rose-50/50 p-4 text-xs text-rose-800 border border-rose-100 space-y-1">
              <p className="font-semibold flex items-center gap-1.5 text-rose-900">
                <Sparkles className="h-4 w-4 text-rose-500" /> Catatan Refleksi Pernikahan:
              </p>
              <p>
                Tujuan Couple Talk bukanlah persaingan angka, melainkan saling mendengarkan secara tulus. Setiap obrolan hangat hari ini adalah pondasi kokoh untuk pernikahan kalian kelak.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button onClick={() => setView("lobby")} className="flex-1 bg-rose-600 hover:bg-rose-700 text-white">
                <RotateCcw className="mr-2 h-4 w-4" /> Main Lagi
              </Button>
              <Button variant="outline" onClick={fetchHistory} className="flex-1 border-slate-200">
                <History className="mr-2 h-4 w-4" /> Lihat Riwayat
              </Button>
              <Button variant="ghost" onClick={() => navigate("/dashboard")} className="flex-1 text-slate-600">
                Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== VIEW: GAME (ACTIVE CARD) ====================
  if (view === "game") {
    if (!activeSession) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center space-y-4 text-center">
          <Sparkles className="h-10 w-10 animate-spin text-rose-500" />
          <p className="text-sm font-medium text-slate-700">Memuat sesi permainan...</p>
          <Button variant="outline" onClick={() => setView("lobby")}>Kembali ke Lobby</Button>
        </div>
      );
    }

    const cards = activeSession.cards || [];
    const currIdx = activeSession.current_card_index ?? 0;
    const isCompleted = currIdx >= cards.length;

    if (isCompleted) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center space-y-4">
          <CheckCircle2 className="h-12 w-12 text-emerald-500" />
          <p className="text-base font-semibold text-slate-800">Semua kartu telah dimainkan!</p>
          <Button onClick={() => fetchRecap(activeSession.session_id)} className="bg-rose-600 hover:bg-rose-700 text-white">
            Lihat Rekap Akhir
          </Button>
        </div>
      );
    }

    const currentCard = cards[currIdx];
    if (!currentCard) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center space-y-4">
          <Sparkles className="h-10 w-10 animate-spin text-rose-500" />
          <p className="text-sm font-medium text-slate-600">Memuat kartu permainan...</p>
          <Button variant="outline" onClick={() => setView("lobby")}>Kembali ke Lobby</Button>
        </div>
      );
    }

    const isAskingPlayer = currentCard.asking_user_id === user?.user_id || currentCard.asking_user_id === "usr_partner_placeholder";
    const progressPercent = Math.round(((currIdx + 1) / cards.length) * 100);

    return (
      <div className="mx-auto max-w-2xl space-y-5 px-4 sm:px-6 pb-24 sm:pb-12">
        {/* Game Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setView("lobby")} className="text-slate-600">
            <ArrowLeft className="mr-1 h-4 w-4" /> Keluar ke Lobby
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600">Kartu {currIdx + 1} dari {cards.length}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEndDialogOpen(true)}
              className="border-rose-200 text-rose-700 hover:bg-rose-50 text-xs h-8 ml-2"
            >
              <X className="mr-1 h-3.5 w-3.5" /> Akhiri Permainan
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-1">
          <Progress value={progressPercent} className="h-2 bg-rose-100" />
          <p className="text-right text-[11px] font-medium text-slate-500">{progressPercent}% Selesai</p>
        </div>

        {/* Turn Banner */}
        <div className="rounded-xl border border-rose-200 bg-gradient-to-r from-rose-50 via-pink-50 to-rose-50 p-4 text-center shadow-sm">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-rose-700">
            <Flame className="h-4 w-4 text-rose-500" />
            <span>Penanya: {currentCard.asking_user_name}</span>
            <span>→</span>
            <span>Penjawab: {currentCard.answering_user_name}</span>
          </div>
          <p className="mt-1 text-xs text-slate-600 font-medium">
            {isAskingPlayer
              ? "🗣️ Ini giliranmu membaca pertanyaan dan memberikan rating!"
              : "👂 Dengarkan pertanyaan yang dibacakan oleh pasanganmu."}
          </p>
        </div>

        {/* 3D Physical Card Deck Arena */}
        <div className="relative w-full [perspective:1200px] min-h-[420px] sm:min-h-[460px] flex items-center justify-center my-2 sm:my-4">
          {/* Deck Stack Effect (Kartu di Bawah Tumpukan) */}
          <div className="absolute inset-x-2 bottom-0 top-3 rounded-2xl bg-rose-800/40 border-2 border-rose-400/40 shadow-sm translate-y-3 scale-95 pointer-events-none" />
          <div className="absolute inset-x-1 bottom-0 top-1.5 rounded-2xl bg-rose-700/60 border-2 border-rose-300/60 shadow-md translate-y-1.5 scale-[0.975] pointer-events-none" />

          {/* Active Flipping 3D Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currIdx}
              initial={{ y: 80, scale: 0.8, opacity: 0, rotateY: 180 }}
              animate={{ y: 0, scale: 1, opacity: 1, rotateY: isFlipped ? 0 : 180 }}
              exit={{ y: -80, scale: 0.8, opacity: 0, rotateY: 0 }}
              transition={{ duration: 0.6, type: "spring", stiffness: 200, damping: 20 }}
              style={{ transformStyle: "preserve-3d" }}
              className="w-full relative rounded-2xl cursor-pointer select-none min-h-[420px] sm:min-h-[460px]"
            >
              {/* BACK OF CARD (Sisi Belakang Kartu Deck) */}
              <div
                onClick={() => isAskingPlayer && setIsFlipped(true)}
                className="absolute inset-0 w-full h-full rounded-2xl border-2 border-rose-300 bg-gradient-to-br from-rose-600 via-pink-600 to-rose-800 p-6 sm:p-8 text-white shadow-2xl flex flex-col items-center justify-center text-center space-y-4 sm:space-y-5"
                style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
              >
                <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white/20 flex items-center justify-center text-white border-2 border-white/30 shadow-inner">
                  {isAskingPlayer ? (
                    <HeartHandshake className="h-9 w-9 sm:h-11 sm:w-11 animate-pulse" />
                  ) : (
                    <Lock className="h-9 w-9 sm:h-11 sm:w-11 text-white/90" />
                  )}
                </div>
                <div className="space-y-1">
                  <h3 className="font-serif text-xl sm:text-2xl font-bold tracking-wide">Weddly Couple Talk</h3>
                  <Badge className="bg-white/20 text-white border-none text-xs px-3 py-1">
                    Kartu #{currIdx + 1}
                  </Badge>
                </div>
                {isAskingPlayer ? (
                  <p className="text-xs text-rose-100 font-medium bg-black/20 px-4 py-2 rounded-full border border-white/20">
                    ✨ Ketuk kartu untuk membuka pertanyaan 🎴
                  </p>
                ) : (
                  <p className="text-xs text-rose-100 font-semibold bg-black/30 px-4 py-2 rounded-full border border-white/30 flex items-center gap-1.5">
                    <Lock className="h-3.5 w-3.5" /> Menunggu {currentCard.asking_user_name} membaca pertanyaan
                  </p>
                )}
              </div>

              {/* FRONT OF CARD (Sisi Depan Kartu Pertanyaan) */}
              <div
                className="w-full min-h-[420px] sm:min-h-[460px] rounded-2xl border-2 border-rose-200 bg-white p-5 sm:p-6 shadow-2xl flex flex-col justify-between text-center"
                style={{ backfaceVisibility: "hidden" }}
              >
                {/* Header Badge */}
                <div className="flex items-center justify-between border-b border-rose-100 pb-3">
                  <Badge variant="secondary" className="bg-rose-100 text-rose-700 hover:bg-rose-100">
                    {currentCard.category_name}
                  </Badge>
                  <button
                    type="button"
                    onClick={() => setIsFlipped(false)}
                    className="text-xs text-rose-600 font-semibold hover:underline flex items-center gap-1"
                  >
                    🎴 Balikkan Kartu
                  </button>
                </div>

                {/* Question Body */}
                <div className="my-auto py-4 space-y-5">
                  <p className="text-xl sm:text-2xl font-bold text-slate-800 leading-relaxed px-2">
                    &ldquo;{currentCard.question_text}&rdquo;
                  </p>

                  <div className="mx-auto max-w-md rounded-lg bg-slate-50 p-3 text-xs text-slate-600 border border-slate-100">
                    💡 <span className="font-medium text-slate-700">Petunjuk:</span> Bacakan pertanyaan ini kepada pasanganmu secara lisan, lalu dengarkan jawabannya dengan penuh perhatian.
                  </div>

                  {!isAskingPlayer && (
                    <div className="mx-auto max-w-md rounded-lg bg-amber-50 p-3 text-xs font-semibold text-amber-800 border border-amber-200 flex items-center justify-center gap-2">
                      <Lock className="h-4 w-4 text-amber-600 shrink-0" />
                      <span>Menunggu {currentCard.asking_user_name} membacakan pertanyaan & memberikan nilai...</span>
                    </div>
                  )}
                </div>

                {/* Actions for Asking Player */}
                <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setRatingDialogOpen(true);
                    }}
                    disabled={submitting || !isAskingPlayer}
                    className="w-full sm:w-auto px-6 bg-rose-600 hover:bg-rose-700 text-white font-semibold shadow-md disabled:opacity-50"
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" /> Sudah Menjawab
                  </Button>

                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeclineDialogOpen(true);
                    }}
                    disabled={submitting || !isAskingPlayer}
                    className="w-full sm:w-auto border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                  >
                    <ShieldAlert className="mr-1.5 h-4 w-4 text-rose-500" /> Tidak Menjawab (-7)
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGameAction("skip");
                    }}
                    disabled={submitting || !isAskingPlayer}
                    className="w-full sm:w-auto text-slate-500 hover:text-slate-700 disabled:opacity-50"
                  >
                    Lewati Pertanyaan (-5)
                  </Button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Rating Dialog */}
        <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center text-lg text-slate-800">
                Beri Rating Jawaban {currentCard.answering_user_name}
              </DialogTitle>
              <DialogDescription className="text-center text-xs text-slate-500">
                Pilih emoji rating yang mencerminkan keterbukaan dan kehangatan jawaban pasanganmu. Poin akan masuk ke akun {currentCard.answering_user_name}.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3 py-4">
              {EMOJI_RATINGS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setSelectedRating(item.value)}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl border transition-all ${
                    selectedRating === item.value
                      ? "border-rose-500 bg-rose-50/80 shadow-sm ring-2 ring-rose-300"
                      : "border-slate-200 hover:border-rose-200 hover:bg-slate-50"
                  }`}
                >
                  <span className="text-3xl mb-1">{item.emoji}</span>
                  <span className="text-xs font-semibold text-slate-800">{item.label}</span>
                  <span className="text-[10px] text-rose-600 font-bold mt-0.5">{item.desc}</span>
                </button>
              ))}
            </div>

            <DialogFooter>
              <Button
                onClick={() => handleGameAction("answer", selectedRating)}
                disabled={submitting}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold"
              >
                Simpan Rating & Lanjut
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Penalty Confirmation Dialog */}
        <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-slate-800 flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-rose-600" /> Konfirmasi Penalti Tidak Menjawab
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-600 pt-2">
                Menandai pertanyaan ini sebagai <span className="font-semibold text-rose-600">&ldquo;Tidak Menjawab&rdquo;</span> akan mengurangi skor <span className="font-semibold text-slate-800">{currentCard.answering_user_name}</span> sebanyak <span className="font-bold text-rose-600">-7 poin</span>.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0 pt-4">
              <Button variant="outline" onClick={() => setDeclineDialogOpen(false)}>
                Batal
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleGameAction("decline")}
                disabled={submitting}
                className="bg-rose-600 hover:bg-rose-700"
              >
                Ya, Kurangi 7 Poin
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ==================== VIEW: HISTORY ====================
  if (view === "history") {
    return (
      <div className="mx-auto max-w-3xl space-y-6 px-4 sm:px-6 pb-24 sm:pb-12">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Riwayat Sesi Couple Talk</h2>
            <p className="text-xs text-slate-500">Daftar permainan yang telah selesai dimainkan bersama pasangan.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setView("lobby")}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Kembali
          </Button>
        </div>

        {historyList.length === 0 ? (
          <Card className="p-8 text-center border-dashed">
            <History className="mx-auto h-10 w-10 text-slate-300 mb-2" />
            <p className="text-sm font-semibold text-slate-700">Belum Ada Riwayat Permainan</p>
            <p className="text-xs text-slate-500 mt-1">Selesaikan 20 kartu pertanyaan untuk melihat hasil rekap di sini.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {historyList.map((item) => (
              <Card key={item.session_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-slate-800">
                      {item.player_a_name} ({item.player_a_score} Poin) vs {item.player_b_name} ({item.player_b_score} Poin)
                    </p>
                    <p className="text-xs text-slate-500">
                      Selesai pada: {new Date(item.completed_at || item.updated_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchRecap(item.session_id)}
                    className="border-rose-200 text-rose-700 hover:bg-rose-50"
                  >
                    Lihat Rekap <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ==================== VIEW: LOBBY (DEFAULT) ====================
  return (
    <div className="mx-auto max-w-4xl space-y-6 sm:space-y-8 px-4 sm:px-6 pb-24 sm:pb-12">
      {/* Hero Header */}
      <div className="rounded-2xl bg-gradient-to-br from-rose-500 via-pink-600 to-rose-700 p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute right-0 top-0 -mt-8 -mr-8 h-48 w-48 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        <div className="relative z-10 max-w-xl space-y-3">
          <Badge className="bg-white/20 text-white hover:bg-white/30 border-none px-3 py-1">
            <HeartHandshake className="mr-1.5 h-3.5 w-3.5" /> Couple Game Mode
          </Badge>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Couple Talk
          </h1>
          <p className="text-xs sm:text-sm text-rose-100 leading-relaxed">
            Permainan 20 kartu pertanyaan untuk membangun komunikasi mendalam, saling memahami emosi, dan mempersiapkan kehidupan pernikahan bersama pasangan.
          </p>
        </div>
      </div>

      {/* Unlinked Partner Warning */}
      {!hasPartners && (
        <Card className="border-amber-200 bg-amber-50/70 shadow-sm">
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-amber-900">Mode Tatap Muka (Pasangan Belum Terhubung)</p>
                <p className="text-xs text-amber-700">
                  Anda tetap dapat memainkan Couple Talk bersama Pasangan secara langsung di perangkat ini. Untuk akses mandiri dari 2 HP terpisah, hubungkan akun pasangan di menu Pengaturan.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => navigate("/settings")}
              className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap text-xs shrink-0"
            >
              <UserCheck className="mr-1.5 h-4 w-4" /> Hubungkan Pasangan
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active Session Banner (If Unfinished Game Exists) */}
      {activeSession && (
        <Card className="border-rose-200 bg-gradient-to-r from-rose-50 to-pink-50 shadow-sm">
          <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="space-y-1 text-center sm:text-left">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-700">
                <Sparkles className="h-4 w-4 text-rose-500" /> Permainan Belum Selesai Tersedia!
              </span>
              <p className="text-xs text-slate-600">
                Kartu {activeSession.current_card_index + 1} dari {activeSession.cards?.length || 20} · {activeSession.player_a_name} & {activeSession.player_b_name}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setView("game")}
                className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-4"
              >
                <Play className="mr-1.5 h-4 w-4" /> Lanjutkan Permainan
              </Button>
              <Button
                variant="outline"
                onClick={handleEndGame}
                disabled={submitting}
                className="border-rose-200 text-rose-700 hover:bg-rose-50 text-xs font-semibold px-3"
              >
                <X className="mr-1 h-3.5 w-3.5" /> Akhiri Sesi
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Controls & Category Selection */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <div>
                <CardTitle className="text-base font-bold text-slate-800">1. Pilih 1 Topik Obrolan</CardTitle>
                <CardDescription className="text-xs text-slate-500">Pilih 1 kategori topik untuk menyusun 20 kartu pertanyaan (10 kartu untuk masing-masing pasangan).</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {categories.map((cat) => {
                const isSelected = selectedCat === cat.id;
                const IconComp = getCategoryIcon(cat.icon);
                const isTrialUser = !isAdmin && user?.plan_type === "trial";
                const isLocked = cat.is_locked || (isTrialUser && cat.id !== "relationship");

                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      if (isLocked) {
                        setPaymodalOpen(true);
                        return;
                      }
                      setSelectedCat(cat.id);
                    }}
                    className={`relative flex items-start gap-3 p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                      isLocked
                        ? "border-amber-200/80 bg-amber-50/40 hover:bg-amber-50/70 opacity-90"
                        : isSelected
                        ? "border-rose-500 bg-rose-50/80 shadow-sm ring-2 ring-rose-300"
                        : "border-slate-200 bg-white hover:border-rose-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className={`p-2.5 rounded-lg shrink-0 ${
                      isLocked
                        ? "bg-amber-100 text-amber-700"
                        : isSelected
                        ? "bg-rose-500 text-white"
                        : "bg-slate-100 text-slate-500"
                    }`}>
                      <IconComp className="h-4 w-4" />
                    </div>
                    <div className="space-y-0.5 flex-1 pr-6">
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-bold text-slate-800">{cat.name}</p>
                        {isLocked && (
                          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] px-1.5 py-0 font-semibold flex items-center gap-0.5">
                            <Lock className="h-2.5 w-2.5" /> Premium
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 leading-tight line-clamp-2">{cat.description}</p>
                    </div>
                    <div className="absolute top-3 right-3">
                      {isLocked ? (
                        <div className="h-5 w-5 rounded-full flex items-center justify-center bg-amber-100 border border-amber-300 text-amber-700">
                          <Lock className="h-3 w-3" />
                        </div>
                      ) : (
                        <div className={`h-5 w-5 rounded-full flex items-center justify-center border transition-all ${
                          isSelected ? "bg-rose-500 border-rose-500 text-white shadow-xs" : "border-slate-300 bg-white text-transparent"
                        }`}>
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Action Row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              onClick={handleStartGame}
              disabled={submitting}
              className="flex-1 bg-rose-600 hover:bg-rose-700 text-white font-bold shadow-md min-h-[48px] h-auto py-3 px-4 text-sm sm:text-base leading-snug flex items-center justify-center"
            >
              <Play className="mr-2 h-5 w-5 shrink-0" /> Mulai Permainan Baru (20 Kartu)
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={fetchHistory}
              className="border-slate-200 text-slate-700 hover:bg-slate-50 min-h-[48px] h-auto py-3 px-4 text-sm sm:text-base leading-snug flex items-center justify-center"
            >
              <History className="mr-2 h-5 w-5 shrink-0" /> Riwayat Game
            </Button>
          </div>
        </div>

        {/* Rules & Guide Sidebar */}
        <div className="space-y-4">
          <Card className="border-rose-100 bg-rose-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1.5">
                <HelpCircle className="h-4 w-4 text-rose-500" /> Cara Bermain Couple Talk
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs text-slate-600">
              <div className="flex items-start gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-[10px] font-bold text-rose-700">1</span>
                <p><span className="font-semibold text-slate-800">Pilih Topik:</span> Tentukan kategori obrolan yang ingin dibahas berdua.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-[10px] font-bold text-rose-700">2</span>
                <p><span className="font-semibold text-slate-800">Bicara Langsung:</span> Pemain Penanya membaca kartu dengan nyaring, Pemain Penjawab menjawab secara lisan (tanpa diketik).</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-[10px] font-bold text-rose-700">3</span>
                <p><span className="font-semibold text-slate-800">Rating Emoji:</span> Penanya memberikan rating emoji (1–4 poin) yang akan menjadi skor untuk Penjawab.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-[10px] font-bold text-rose-700">4</span>
                <p><span className="font-semibold text-slate-800">Aturan Penalti:</span> Penanya bisa memilih "Tidak Menjawab" (-7 poin penalti) atau "Lewati Pertanyaan" (-5 poin penalti).</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-[10px] font-bold text-rose-700">5</span>
                <p><span className="font-semibold text-slate-800">Recap Pasangan:</span> Setelah 20 kartu selesai, lihat rekap statistik dan apresiasi hubungan kalian.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Partner Invitation Dialog */}
      <Dialog
        open={invitationOpen}
        onOpenChange={(open) => {
          setInvitationOpen(open);
          if (!open && activeSession) {
            markInviteDismissed(activeSession.session_id);
          }
        }}
      >
        <DialogContent className="sm:max-w-md border-rose-200 bg-gradient-to-b from-rose-50/80 to-white text-center">
          <DialogHeader className="text-center space-y-2">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-100 text-rose-600 animate-bounce">
              <HeartHandshake className="h-8 w-8" />
            </div>
            <DialogTitle className="text-xl font-bold text-slate-800">
              Undangan Permainan Couple Talk! 💌
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600 leading-relaxed">
              Pasanganmu <span className="font-bold text-rose-600">{activeSession?.player_a_name}</span> telah memulai permainan Couple Talk 20 kartu! Ayo gabung dan mulai mengobrol bersama.
            </DialogDescription>
          </DialogHeader>

          <div className="my-2 rounded-lg bg-white p-3 border border-rose-100 text-xs space-y-1 text-left">
            <div className="flex justify-between">
              <span className="text-slate-500">Pemain A (Penanya Pertama):</span>
              <span className="font-semibold text-slate-800">{activeSession?.player_a_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Pemain B (Penjawab Pertama):</span>
              <span className="font-semibold text-slate-800">{activeSession?.player_b_name}</span>
            </div>
          </div>

          <DialogFooter className="sm:justify-center pt-2">
            <Button
              onClick={() => {
                if (activeSession) {
                  markInviteDismissed(activeSession.session_id);
                }
                setInvitationOpen(false);
                setView("game");
              }}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold h-11 shadow-md"
            >
              <Play className="mr-2 h-4 w-4" /> Gabung Permainan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Game Confirmation Dialog */}
      <Dialog open={endDialogOpen} onOpenChange={setEndDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-600" /> Akhiri Permainan?
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600 pt-2">
              Apakah Anda yakin ingin mengakhiri sesi permainan Couple Talk saat ini? Sesi akan dihentikan dan Anda dapat memulai permainan baru dari awal.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0 pt-4">
            <Button variant="outline" onClick={() => setEndDialogOpen(false)}>
              Batal
            </Button>
            <Button
              variant="destructive"
              onClick={handleEndGame}
              disabled={submitting}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Ya, Akhiri Permainan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Premium Upgrade Modal */}
      <PremiumPaymentModal open={paymodalOpen} onOpenChange={setPaymodalOpen} />
    </div>
  );
}
